/**
 * Wires `sellWizard.model.ts` to the buyer check and the one write.
 *
 * Calls `checkBuyer` on blur of the id field — never per keystroke, since
 * every refusal spends one of a small hourly probe budget — and `effectSale`
 * on Convert, after which `onSold` hands the inventory its re-read. The
 * dialog stays mounted when shut, so `work` is nullable and the draft is
 * built during render rather than in an effect.
 *
 * The state the model cannot hold is the current step, the drafts keyed by
 * consignment so a buyer never carries across works, the buyer verdict
 * keyed by the id it was about, and the submitting/error pair. Closing puts
 * a draft down; only a successful sale or opening a different work drops it.
 * The error is cleared on every step change, because a refusal describes the
 * act that raised it and not the step it happens to be rendered on.
 */

import { useCallback, useMemo, useState } from "react";
import type { ConsignedWork } from "@ondo/shared";

import { checkBuyer, effectSale } from "../lib/api.ts";
import { SELL_COPY } from "./sellWizard.constants.ts";
import {
  SELL_STEPS,
  canContinue,
  consignmentFacts,
  describeSaleError,
  feeFor,
  initialDraft,
  namedBuyer,
  nextStep,
  previousStep,
  summaryRows,
  toEffectSaleRequest,
} from "./sellWizard.model.ts";
import type { ConsignmentFact, SellDraft, SellStep } from "./sellWizard.model.ts";

/** Everything the wizard renders, already decided. */
/**
 * What the registry has said about the id in the buyer field.
 *
 * Carries the ONDO ID IT IS ABOUT on every state, so a verdict can never be
 * rendered under a different id than it was asked about. A stale "Sarah
 * Okonkwo" under a half-retyped id is the worst state this step can be in, and
 * it is the state that would let somebody confirm the wrong buyer.
 */
export type BuyerCheck =
  | { state: "checking"; ondoId: string }
  | { state: "found"; ondoId: string; displayName: string; confirmed: boolean }
  | { state: "refused"; ondoId: string; message: string };

export interface SellWizardView {
  open: boolean;
  /** The work being sold, or null when the dialog is shut but still mounted. */
  work: ConsignedWork | null;
  step: SellStep;
  /** Which steps are behind the reader, for the ticks on the stepper. */
  done: readonly SellStep[];
  draft: SellDraft;
  facts: ConsignmentFact[];
  summary: ConsignmentFact[];
  fee: { kind: "plain" | "premium"; amount: string };
  /** Whether the current step may be left. The view disables Continue on it. */
  canAdvance: boolean;
  /** What the registry says about the typed ONDO id, or null before it is asked. */
  check: BuyerCheck | null;
  /** What Convert will actually do — which depends on whether the buyer is on ONDO. */
  effect: string;
  submitting: boolean;
  error: string | null;
}

export interface SellWizardController {
  view: SellWizardView;
  setField: <K extends keyof SellDraft>(key: K, value: SellDraft[K]) => void;
  next: () => void;
  back: () => void;
  submit: () => void;
  /** Ask the registry who this id is. Called on blur, never on every keystroke. */
  checkOndoId: () => void;
  /** The director agreeing that the name shown is the buyer they meant. */
  confirmBuyer: (confirmed: boolean) => void;
  /** Puts the draft down. Escape, the backdrop and ✕ — it does not discard. */
  close: () => void;
}

/**
 * The five steps, the draft, and the one write.
 *
 * THE DRAFT SURVIVES A CLOSE, and the argument is `AddHistoryDialog`'s: a stray
 * Escape must not cost somebody a price they negotiated for a month. It is reset
 * when a DIFFERENT work is opened, and after a sale succeeds — never by closing.
 *
 * AFTER A SALE IT RE-READS RATHER THAN PATCHING, which is `useInventoryController`
 * .answer's argument one step further on: `ondo.effect_sale()` ends the
 * consignment, so the sold row does not change on the inventory — it leaves it,
 * and reappears on a different screen fed by a different relation. Local state
 * would have to guess at both halves of that.
 *
 * @param work the consignment being sold, or null when nothing is open
 * @param onSold called after the write commits, so the inventory can re-read
 * @param onClose puts the dialog down; the draft survives it
 */
export function useSellWizardController(
  work: ConsignedWork | null,
  onSold: () => void,
  onClose: () => void,
): SellWizardController {
  const [step, setStep] = useState<SellStep>("consignment");
  const [drafts, setDrafts] = useState<Map<string, SellDraft>>(new Map());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [check, setCheck] = useState<BuyerCheck | null>(null);

  // Keyed by consignment so switching works does not carry a buyer across, and
  // reopening the same one picks the draft back up. `initialDraft` is called
  // during render rather than in an effect: a form whose first paint is empty
  // and whose second is prefilled is a form that loses a keystroke.
  const draft = useMemo(
    () => (work ? (drafts.get(work.consignmentId) ?? initialDraft(work)) : initialDraft(null)),
    [work, drafts],
  );

  const setField = useCallback(
    <K extends keyof SellDraft>(key: K, value: SellDraft[K]) => {
      if (!work) return;

      // The verdict was about the id that has just changed, so it is dropped
      // rather than left to describe a different one — and dropping it un-gates
      // Continue, because `canContinue` compares the CONFIRMED id against the
      // typed one. Switching the toggle drops it too: the field being read has
      // changed even though neither field has.
      if (key === "buyerOndoId" || key === "buyerMode") setCheck(null);

      setDrafts((held) => {
        const next = new Map(held);
        next.set(work.consignmentId, {
          ...(held.get(work.consignmentId) ?? initialDraft(work)),
          [key]: value,
        });
        return next;
      });
    },
    [work],
  );

  /**
   * Ask the registry who this id is.
   *
   * ON BLUR, never per keystroke: every refusal spends one of ten probes an
   * hour, and a field checked as it is typed would burn the budget on the
   * prefixes of a correct id. A malformed id is refused by shape before it
   * reaches the server for the same reason — asking would spend a probe to be
   * told what the regex already knows.
   */
  const checkOndoId = useCallback(() => {
    if (!work) return;

    const typed = draft.buyerOndoId.trim().toUpperCase();
    if (draft.buyerMode !== "ondo" || !ONDO_ID.test(typed)) return;
    // Already answered for this exact id. Re-blurring a confirmed field must not
    // spend the budget again, nor silently un-tick what was agreed.
    if (check && check.ondoId === typed && check.state !== "checking") return;

    setCheck({ state: "checking", ondoId: typed });

    checkBuyer(work.consignmentId, { ondoId: typed })
      .then((answer) => {
        setCheck(
          answer.verdict === "eligible" && answer.principal
            ? {
                state: "found",
                ondoId: typed,
                displayName: answer.principal.displayName,
                // Never pre-ticked. The whole point of the step is that somebody
                // reads the name; a box already agreed to is not a confirmation.
                confirmed: false,
              }
            : {
                state: "refused",
                ondoId: typed,
                // The server's sentence, never one written here: it is the same
                // string the sale raises, so pressing on does not produce a
                // second explanation of one refusal.
                message: answer.message ?? SELL_COPY.errors.refused,
              },
        );
      })
      // A failed CHECK is reported, unlike the agent dialog's, because here it
      // is a GATE rather than a courtesy: a director who is never told the
      // question failed would sit in front of a Continue button that will not
      // light and no reason why.
      .catch((cause: unknown) => {
        setCheck({ state: "refused", ondoId: typed, message: describeSaleError(cause) });
      });
  }, [work, draft.buyerOndoId, draft.buyerMode, check]);

  const confirmBuyer = useCallback((confirmed: boolean) => {
    setCheck((at) => (at?.state === "found" ? { ...at, confirmed } : at));
  }, []);

  // THE ERROR IS CLEARED ON EVERY MOVE, and that is a fix rather than a tidy-up.
  // A submit refusal rendered in the body outlived the step it was raised on, so
  // pressing Back put "no ONDO id ... that this work can be transferred to"
  // under the buyer form as if it were that step's own complaint about the
  // field. A refusal describes the act that raised it and nothing else.
  const next = useCallback(() => {
    setError(null);
    setStep(nextStep);
  }, []);

  const back = useCallback(() => {
    setError(null);
    setStep(previousStep);
  }, []);

  // Puts the draft DOWN rather than throwing it away — the draft map is keyed by
  // consignment and is untouched here, so reopening the same work picks it back
  // up. `AddHistoryDialog` made this argument first: a stray Escape must not cost
  // somebody the terms they spent a month agreeing.
  const close = useCallback(() => {
    setError(null);
    setStep("consignment");
    onClose();
  }, [onClose]);

  const submit = useCallback(() => {
    if (!work) return;

    setSubmitting(true);
    setError(null);

    effectSale(work.consignmentId, toEffectSaleRequest(draft))
      .then(() => {
        // The draft goes only once the registry has the sale. Dropping it before
        // the write would lose everything typed on any failure that follows.
        setDrafts((held) => {
          const next = new Map(held);
          next.delete(work.consignmentId);
          return next;
        });
        setCheck(null);
        setStep("consignment");
        onSold();
      })
      .catch((cause: unknown) => {
        setError(describeSaleError(cause));
      })
      .finally(() => {
        setSubmitting(false);
      });
  }, [work, draft, onSold]);

  return {
    view: {
      open: work !== null,
      work,
      step,
      done: SELL_STEPS.slice(0, SELL_STEPS.indexOf(step)),
      draft,
      facts: work ? consignmentFacts(work) : [],
      summary: work ? summaryRows(work, draft) : [],
      fee: feeFor(draft),
      canAdvance: canContinue(step, draft, confirmedOndoId(check)) && !submitting,
      check,
      effect:
        namedBuyer(draft)?.kind === "name"
          ? SELL_COPY.convertEffect.name
          : SELL_COPY.convertEffect.ondo,
      submitting,
      error,
    },
    setField,
    next,
    back,
    submit,
    close,
    checkOndoId,
    confirmBuyer,
  };
}

/**
 * The id the director actually ticked, or null.
 *
 * An id rather than a boolean, so that retyping the field cannot leave a
 * confirmation standing over a different buyer — `canContinue` compares this
 * against what is in the field.
 */
function confirmedOndoId(check: BuyerCheck | null): string | null {
  return check?.state === "found" && check.confirmed ? check.ondoId : null;
}

/** The shape the server's own validator uses. Refused here to save a probe. */
const ONDO_ID = /^ONDO-\d{4,}$/;
